import {
  Fuel,
  MeterBalance,
  TopupHistoryItem,
  TransactionState,
} from '@/src/api/payg/types';
import { formatCurrencyValue } from '@/src/utils/money';
import { formatMonthAndYear } from '@/src/utils/date';

export type MsnFuelMap = Record<string, Fuel>;

/** Builds a lookup of meter serial number to fuel from the account's balances. */
export const buildMsnFuelMap = (balances: MeterBalance[]): MsnFuelMap =>
  balances.reduce<MsnFuelMap>((map, { msn, fuel }) => {
    map[msn] = fuel;
    return map;
  }, {});

export type TopupPresentation = {
  statusLabel?: string;
  statusVariant?: 'info' | 'error';
  showPlus: boolean;
};

/**
 * Derives how a top-up should be presented from its transaction state.
 * Keyed on the top-level `state`: a `SUCCESS` payment was taken (show `+`),
 * `PENDING` is in flight, and only a `FAILURE` (no payment taken) reads as failed.
 */
export const getTopupPresentation = (
  item: TopupHistoryItem,
): TopupPresentation => {
  switch (item.state) {
    case TransactionState.PENDING:
      return {
        statusLabel: 'In progress',
        statusVariant: 'info',
        showPlus: false,
      };
    case TransactionState.FAILURE:
      return {
        statusLabel: 'Failed',
        statusVariant: 'error',
        showPlus: false,
      };
    case TransactionState.SUCCESS:
    default:
      return { showPlus: true };
  }
};

/** Formats a pence amount as GBP, dropping decimals for whole-pound values. */
export const formatTopupAmount = (amountInPence: number): string =>
  formatCurrencyValue(
    { unit: 'GBP', dp: amountInPence % 100 === 0 ? 0 : 2 },
    { amount: amountInPence.toString(), currencyUnit: 'GBX' },
  );

export type TopupMonthGroup = {
  monthLabel: string;
  items: TopupHistoryItem[];
};

const byDateTimeDescending = (a: TopupHistoryItem, b: TopupHistoryItem) =>
  b.transactionDateTime.localeCompare(a.transactionDateTime);

/**
 * Groups top-ups by calendar month, with the most recent month — and the most
 * recent item within each month — first.
 */
export const groupTopupsByMonth = (
  items: TopupHistoryItem[],
): TopupMonthGroup[] => {
  const groups = new Map<string, TopupMonthGroup>();

  [...items].sort(byDateTimeDescending).forEach(item => {
    const date = new Date(item.transactionDateTime);
    const key = `${date.getFullYear()}-${date.getMonth()}`;

    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, { monthLabel: formatMonthAndYear(date), items: [item] });
    }
  });

  return [...groups.values()];
};
